/**
 * Users → Activity: the view's palette, grid styling and cell renderers.
 *
 * Split out of the screen because the same renderers are used by the master
 * grids AND by both expanded grids — a figure has to sit in the same place, in
 * the same treatment, wherever you read it.
 */
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { slate, teal } from "../../../theme/colors";
import {
  OTHER_BAR_COLOR,
  OTHER_SERIES,
  SERIES,
  VIEW_BAR_COLOR,
  VIEW_SERIES,
  fullDate,
  isSystem,
  longDay,
  num,
  relativeDayUtc,
} from "./userActivityModel";

// The report palette — every value an alias onto the color system, so this
// view can't drift from the rest of the product.
export const S = {
  surface: "#FFFFFF",
  border: slate[200],
  label: slate[400], // small-caps header text
  text: slate[800], // figures
  muted: slate[500], // the date column, block captions
  faint: slate[300], // a zero, rendered as a dash — the lightest thing here
  sub: slate[400], // secondary text: the email, the hints beside a count
  accent: teal[600], // edits — the one number that carries color
  hover: "#F8FAFB",
  sysRow: "#FAFBFC",
  detailBg: "#FBFCFD",
  track: "#EEF1F5", // the empty part of a bar
  band: slate[50], // the group header band, the stat strip
};

export const fmtInt = (n) => num(n).toLocaleString("en-US");

// ── cells ───────────────────────────────────────────────────────────────────

/**
 * One numeric cell. A zero is a faint dash rather than a "0" so the eye lands
 * on the columns that actually carry work; `accent` marks the Edits figure the
 * backend doc says to lead with.
 *
 * Deliberately right-aligned rather than centred (the default for custom
 * cells): these are figures read down a column, so their digits have to line up
 * — hence `tabular-nums` and `justifyContent: flex-end`.
 */
export const CountCell = (accent) => (p) => {
  const n = num(p.value);
  return (
    <Flx ac sx={{ height: "100%", width: "100%", justifyContent: "flex-end" }}>
      <Txt
        sx={{
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          fontWeight: n && accent ? 700 : 400,
          color: n ? (accent ? S.accent : S.text) : S.faint,
        }}
      >
        {n ? fmtInt(n) : "–"}
      </Txt>
    </Flx>
  );
};

/**
 * The Edits total, with a mini-bar scaled against the busiest PERSON in the
 * list — the number answers "how much", the bar answers "compared to whom"
 * without making you read the column.
 *
 * `maxEdits` is stamped on master rows only, so the expanded rows below get the
 * number alone. Automation is excluded from the scale (and gets no bar): one
 * sync account runs 20× any human and would flatten every real bar to a pixel.
 */
export const EditsCell = (p) => {
  const n = num(p.value);
  const max = num(p.data?.maxEdits);
  const bar = max > 0 && !isSystem(p.data);
  return (
    <Flx
      ac
      g={1.125}
      sx={{ height: "100%", width: "100%", justifyContent: "flex-end" }}
    >
      <Txt
        sx={{
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          fontWeight: n ? 700 : 400,
          color: n ? S.accent : S.faint,
        }}
      >
        {n ? fmtInt(n) : "–"}
      </Txt>
      {bar ? (
        <Box
          sx={{
            width: 56,
            height: 5,
            flexShrink: 0,
            borderRadius: "3px",
            bgcolor: S.track,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: "3px",
              bgcolor: S.accent,
              width: `${Math.max(2, Math.min(100, (n / max) * 100))}%`,
            }}
          />
        </Box>
      ) : null}
    </Flx>
  );
};

/**
 * The hover card behind a breakdown bar — all twelve categories, named and
 * counted, in three labelled bands. The bar itself collapses "other" and
 * "views" into one slice each to stay readable; this is where the detail lives.
 */
const BreakdownTooltip = ({ row }) => (
  <Box sx={{ py: 0.25 }}>
    {[
      { heading: "Edits", items: SERIES },
      { heading: "Other activity", items: OTHER_SERIES },
      { heading: "Reads", items: VIEW_SERIES },
    ].map((band) => (
      <Box key={band.heading} sx={{ "&:not(:first-of-type)": { mt: 0.75 } }}>
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            opacity: 0.55,
          }}
        >
          {band.heading}
        </Box>
        {band.items.map((s) => ({ ...s, value: num(row[s.key]) })).map((s) => (
          <Flx
            key={s.key}
            ac
            jb
            g={2.25}
            sx={{ fontSize: 12, lineHeight: 1.7, whiteSpace: "nowrap" }}
          >
            <Flx ac g={0.75}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "2.5px",
                  bgcolor: s.color,
                  flexShrink: 0,
                }}
              />
              <span>{s.label}</span>
            </Flx>
            <b style={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtInt(s.value)}
            </b>
          </Flx>
        ))}
      </Box>
    ))}
  </Box>
);

/**
 * The row's whole event mix as one 110px bar: the five edit categories in
 * series order, then one slice for everything that isn't editing, then one for
 * reads. It answers "what KIND of work" at a glance, which no column of numbers
 * does — the shape of the bar is the read, and the tooltip carries all twelve
 * exact counts.
 *
 * Other and Views are collapsed to a slice each on purpose: nine grey bands in
 * a 110px bar is noise, and the distinction that matters at a glance is
 * edit / not-edit / read.
 */
export const BreakdownCell = (p) => {
  const row = p.data || {};
  const total = num(row.total) || 1;
  const slices = [
    ...SERIES.map((s) => ({ ...s, value: num(row[s.key]) })),
    { key: "other", label: "Other", color: OTHER_BAR_COLOR, value: num(row.other) },
    { key: "views", label: "Reads", color: VIEW_BAR_COLOR, value: num(row.views) },
  ].filter((s) => s.value > 0);

  return (
    <Flx ac sx={{ height: "100%", width: "100%", justifyContent: "flex-end" }}>
      <Tooltip
        arrow
        placement="left"
        title={<BreakdownTooltip row={row} />}
        componentsProps={{
          tooltip: { sx: { bgcolor: slate[800], maxWidth: "none", px: 1.5 } },
          arrow: { sx: { color: slate[800] } },
        }}
      >
        <Box
          sx={{
            width: 110,
            height: 9,
            borderRadius: "4px",
            bgcolor: "#F0F3F6",
            overflow: "hidden",
            display: "flex",
          }}
        >
          {slices.map((s) => (
            <Box
              key={s.key}
              sx={{ bgcolor: s.color, width: `${(s.value / total) * 100}%` }}
            />
          ))}
        </Box>
      </Tooltip>
    </Flx>
  );
};

/** The automation tag. */
export const SystemChip = () => (
  <Chip
    size="small"
    label="System"
    sx={{
      height: 17,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: ".04em",
      bgcolor: slate[100],
      color: S.muted,
      flexShrink: 0,
      "& .MuiChip-label": { px: 0.875 },
    }}
  />
);

/**
 * Name over email as ONE label — the email a hair under the name on tight
 * leading. With no email (a service account, or a user the backend couldn't
 * resolve) the single line centres in the row instead of hanging at the top.
 */
export const UserIdentity = ({ row, dense }) => {
  const name = row?.userName || row?.user;
  const email = row?.userName && row?.user ? row.user : null;
  return (
    <Flx ac g={0.875} sx={{ minWidth: 0 }}>
      <Flx column sx={{ minWidth: 0, justifyContent: "center" }}>
        <Txt
          sx={{
            fontSize: dense ? 12.5 : 13.5,
            fontWeight: dense ? 500 : 600,
            lineHeight: 1.25,
            color: isSystem(row) ? S.muted : slate[900],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Txt>
        {email ? (
          <Txt
            sx={{
              fontSize: 10.5,
              fontWeight: 400,
              lineHeight: 1.15,
              color: S.sub,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email}
          </Txt>
        ) : null}
      </Flx>
      {isSystem(row) ? <SystemChip /> : null}
    </Flx>
  );
};

export const UserCell = (p) => (
  <Flx ac sx={{ height: "100%", minWidth: 0 }}>
    <UserIdentity row={p.data} />
  </Flx>
);

export const UserCellDense = (p) => (
  <Flx ac sx={{ height: "100%", minWidth: 0 }}>
    <UserIdentity row={p.data} dense />
  </Flx>
);

/**
 * "Today · Sun, Aug 24" for the two days everyone thinks in; the date alone
 * otherwise.
 *
 * Today/Yesterday are resolved in **UTC**, because that is what this row is —
 * the backend buckets on `ts[:10]` of a UTC timestamp. Comparing the bucket
 * against the browser's local date is a category error: at 10:30 PM Eastern the
 * newest bucket is already the next UTC day, and local-date logic labelled it
 * as neither Today nor Yesterday, so it read as tomorrow. (The Timeline regroups
 * to real Eastern days — see UserActivityTimeline.js.)
 */
export const DayCell = (p) => {
  const iso = p.data?.date;
  const rel = relativeDayUtc(iso);
  return (
    <Flx
      ac
      g={0.625}
      title={longDay(iso)}
      sx={{ height: "100%", minWidth: 0, overflow: "hidden" }}
    >
      <Txt
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: slate[900],
          whiteSpace: "nowrap",
        }}
      >
        {rel || longDay(iso)}
      </Txt>
      {rel ? (
        <Txt
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: S.sub,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          · {longDay(iso)}
        </Txt>
      ) : null}
    </Flx>
  );
};

/** The plain date, for the rows expanded under a person. */
export const DateTextCell = (p) => (
  <Flx ac sx={{ height: "100%", minWidth: 0, overflow: "hidden" }}>
    <Txt
      sx={{
        fontSize: 12.5,
        color: S.muted,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {fullDate(p.value)}
    </Txt>
  </Flx>
);

/** The day's headcount, with automation counted beside it rather than into it. */
export const ActiveUsersCell = (p) => (
  <Flx
    ac
    g={0.5}
    sx={{ height: "100%", width: "100%", justifyContent: "flex-end" }}
  >
    <Txt
      sx={{
        fontSize: 13,
        fontVariantNumeric: "tabular-nums",
        color: num(p.data?.activeUsers) ? S.text : S.faint,
      }}
    >
      {num(p.data?.activeUsers) || "–"}
    </Txt>
    {p.data?.systemCount ? (
      <Txt sx={{ fontSize: 10.5, color: S.sub }}>+{p.data.systemCount} sys</Txt>
    ) : null}
  </Flx>
);

// ── grid styling ────────────────────────────────────────────────────────────
/**
 * The grid's look. Kept out of the shared `adminGrid` because this is the one
 * screen whose table IS the page — ADMIN_GRID_SX is tuned for a small block
 * sitting inside a card.
 */
export const TABLE_SX = {
  "--ag-borders": "none",
  "--ag-row-border-color": S.border,
  "--ag-background-color": S.surface,
  "--ag-odd-row-background-color": S.surface,
  "--ag-header-background-color": S.surface,
  "--ag-header-foreground-color": S.label,
  "--ag-row-hover-color": S.hover,
  "--ag-selected-row-background-color": teal[50],
  "--ag-header-column-separator-display": "none",
  "--ag-cell-horizontal-padding": "14px",
  // AG Grid reserves a min-height on the row viewport so its "no rows" overlay
  // has somewhere to sit, and `domLayout: "autoHeight"` does NOT clear it — so
  // a short table rendered a slab of empty grid under its last row. The screen
  // reserves that room itself, only when there is nothing to show.
  "&& .ag-center-cols-viewport, && .ag-body-viewport, && .ag-center-cols-container":
    { minHeight: 0 },
  ".ag-header": { borderBottom: `1px solid ${S.border}` },
  ".ag-header-cell-text, .ag-header-group-text": {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: S.label,
  },
  // The group row is a BAND, not another header row: tinted, fenced by the same
  // rules its columns carry, and set in sentence case so it reads as the
  // block's name rather than as one more column label.
  ".ag-header-group-cell:not(.ag-header-group-cell-no-group)": {
    background: S.band,
    borderLeft: `1px solid ${S.border}`,
    borderRight: `1px solid ${S.border}`,
  },
  ".ag-header-group-cell-label": { justifyContent: "center" },
  ".ag-header-group-text": {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: ".02em",
    color: slate[600],
  },
  // The rules that fence a block, carried the full height of the table so a
  // number is always visibly inside one block or the other.
  ".ag-cell.edge-l, .ag-header-cell.edge-l": {
    borderLeft: `1px solid ${S.border}`,
  },
  ".ag-cell.edge-r, .ag-header-cell.edge-r": {
    borderRight: `1px solid ${S.border}`,
  },
  // Quartz draws a hairline for every resize handle; across 15 columns that's a
  // picket fence of pips competing with the block rules. Drag still works.
  ".ag-header-cell-resize::after": { display: "none" },
  ".ag-cell": {
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    color: S.text,
    // A grouped cell is wrapped by ag-grid, which moves the `ag-cell-value`
    // class — and the core `overflow:hidden` it carries — off the cell. Put the
    // clip back or a long label paints over the column beside it.
    overflow: "hidden",
  },
  // A cellRenderer is wrapped by AG Grid (and wrapped AGAIN inside a group cell
  // for the expander) — every box in that chain needs a height and a centred
  // cross-axis or a single-line cell hangs at the top of the row.
  ".ag-cell .ag-cell-wrapper": {
    height: "100%",
    width: "100%",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
  },
  ".ag-cell .ag-group-value": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  // Everything but the leading column is a figure: right-aligned, tabular.
  ".ag-cell.num, .ag-cell.num .ag-cell-wrapper": {
    justifyContent: "flex-end",
    fontVariantNumeric: "tabular-nums",
  },
  ".ag-header-cell.num .ag-header-cell-label": { justifyContent: "flex-end" },
  // The expanded grid: a quieter continuation of the row above it, not a card
  // floating inside it.
  ".ag-details-row": {
    padding: 0,
    background: S.detailBg,
    borderBottom: `1px solid ${S.border}`,
  },
  ".ag-details-row .ag-root-wrapper": { border: "none", background: "none" },
  ".ag-details-row .ag-header": {
    background: "none",
    borderBottom: `1px solid ${S.border}`,
  },
  ".ag-details-row .ag-row": { background: "none" },
  ".ag-details-row .ag-cell": { fontSize: "12.5px" },
};
